import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';
import config from '../config';

// Normalize for search: strip diacritics and punctuation so "motorhead"
// matches "Motörhead" and "carissas" matches "Carissa's Wierd".
const normalizeStr = (str) =>
    (str || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/['\u2018\u2019\u201b`\-.,:!?&]/g, '')
        .toLowerCase();

// Single-select searchable picker (used for Festival Series)
function SearchableSelect({ options, value, onChange, placeholder = 'Search...', renderLabel, onCreateItem, createLabel = 'Create new' }) {
    const [query, setQuery] = useState('');
    const [open, setOpen] = useState(false);
    const [focused, setFocused] = useState(false);
    const containerRef = useRef(null);

    const selected = options.find(o => String(o.id) === String(value));
    const inputValue = focused ? query : (selected ? renderLabel(selected) : query);

    const filtered = query.trim()
        ? options.filter(o => normalizeStr(renderLabel(o)).includes(normalizeStr(query)))
        : options.slice(0, 50);

    useEffect(() => {
        function handleClickOutside(e) {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setOpen(false); setFocused(false); setQuery('');
            }
        }
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleInputChange = (e) => { setQuery(e.target.value); setOpen(true); if (!e.target.value) onChange(''); };
    const handleFocus = () => { setFocused(true); setQuery(''); setOpen(true); };
    const handleSelect = (option) => { onChange(String(option.id)); setQuery(''); setOpen(false); setFocused(false); };
    const handleClear = (e) => { e.stopPropagation(); onChange(''); setQuery(''); setOpen(false); setFocused(false); };

    return (
        <div ref={containerRef} style={{ position: 'relative', width: '100%' }}>
            <div style={{ position: 'relative' }}>
                <input
                    type="text"
                    value={inputValue}
                    onChange={handleInputChange}
                    onFocus={handleFocus}
                    placeholder={placeholder}
                    style={{
                        backgroundColor: '#2c2c2c', border: '1px solid #444',
                        borderRadius: open ? '4px 4px 0 0' : '4px',
                        padding: '10px 36px 10px 14px', fontSize: '15px', color: '#e0e0e0',
                        width: '100%', boxSizing: 'border-box', outline: 'none',
                    }}
                    autoComplete="off"
                />
                {value && (
                    <button onClick={handleClear} style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: '#777', cursor: 'pointer', fontSize: '16px', padding: '0' }}>✕</button>
                )}
            </div>
            {open && (
                <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100, backgroundColor: '#2c2c2c', border: '1px solid #444', borderTop: 'none', borderRadius: '0 0 4px 4px', maxHeight: '260px', overflowY: 'auto' }}>
                    {onCreateItem && (
                        <div
                            onMouseDown={(e) => { e.preventDefault(); onCreateItem(query); }}
                            style={{ padding: '9px 14px', fontSize: '13px', color: '#bb86fc', cursor: 'pointer', borderBottom: '1px solid #3a3a3a', display: 'flex', alignItems: 'center', gap: '6px' }}
                            onMouseEnter={e => e.currentTarget.style.backgroundColor = '#2a2040'}
                            onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
                        >
                            <span style={{ fontSize: '15px', lineHeight: 1 }}>&#xFF0B;</span>
                            <span>{createLabel}{query.trim() ? `: "${query.trim()}"` : ''}</span>
                        </div>
                    )}
                    {filtered.length === 0 ? (
                        <div style={{ padding: '10px 14px', color: '#666', fontSize: '14px' }}>No results found</div>
                    ) : (
                        filtered.map(option => (
                            <div
                                key={option.id}
                                onMouseDown={() => handleSelect(option)}
                                style={{ padding: '9px 14px', fontSize: '14px', color: String(option.id) === String(value) ? '#bb86fc' : '#e0e0e0', backgroundColor: String(option.id) === String(value) ? '#1e1e1e' : 'transparent', cursor: 'pointer', borderBottom: '1px solid #333' }}
                                onMouseEnter={e => e.currentTarget.style.backgroundColor = '#383838'}
                                onMouseLeave={e => e.currentTarget.style.backgroundColor = String(option.id) === String(value) ? '#1e1e1e' : 'transparent'}
                            >
                                {renderLabel(option)}
                            </div>
                        ))
                    )}
                </div>
            )}
        </div>
    );
}

// Multi-select searchable picker for promoters
function PromoterPicker({ promoters, selectedIds, onChange }) {
    const [search, setSearch] = useState('');
    const filtered = search
        ? promoters.filter(p => [p.name, p.city].some(v => v && v.toLowerCase().includes(search.toLowerCase())))
        : promoters;

    const toggle = (id) => {
        if (selectedIds.includes(id)) onChange(selectedIds.filter(i => i !== id));
        else onChange([...selectedIds, id]);
    };

    const selectedPromoters = promoters.filter(p => selectedIds.includes(p.id));

    return (
        <div>
            {selectedPromoters.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '8px' }}>
                    {selectedPromoters.map(p => (
                        <div key={p.id} style={{ backgroundColor: '#2c2c2c', border: '1px solid #bb86fc', borderRadius: '4px', padding: '4px 10px', fontSize: '13px', color: '#bb86fc', display: 'flex', alignItems: 'center', gap: '6px' }}>
                            {p.name}
                            <span onClick={() => toggle(p.id)} style={{ cursor: 'pointer', color: '#888' }}>✕</span>
                        </div>
                    ))}
                </div>
            )}
            <div style={{ position: 'relative' }}>
                <input
                    type="text"
                    placeholder="Search promoters..."
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    style={{ backgroundColor: '#2c2c2c', border: '1px solid #444', borderRadius: search && filtered.length > 0 ? '4px 4px 0 0' : '4px', padding: '10px 14px', fontSize: '14px', color: '#e0e0e0', width: '100%', boxSizing: 'border-box' }}
                    autoComplete="off"
                />
                {search && filtered.length > 0 && (
                    <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, backgroundColor: '#2c2c2c', border: '1px solid #444', borderTop: 'none', borderRadius: '0 0 4px 4px', maxHeight: '200px', overflowY: 'auto', zIndex: 100 }}>
                        {filtered.map(p => (
                            <div
                                key={p.id}
                                onMouseDown={() => { toggle(p.id); setSearch(''); }}
                                style={{ padding: '9px 14px', fontSize: '14px', color: selectedIds.includes(p.id) ? '#bb86fc' : '#e0e0e0', cursor: 'pointer', borderBottom: '1px solid #333', backgroundColor: selectedIds.includes(p.id) ? '#1e1e1e' : 'transparent' }}
                                onMouseEnter={e => e.currentTarget.style.backgroundColor = '#383838'}
                                onMouseLeave={e => e.currentTarget.style.backgroundColor = selectedIds.includes(p.id) ? '#1e1e1e' : 'transparent'}
                            >
                                {p.name}{p.city ? ` — ${p.city}` : ''}
                                {selectedIds.includes(p.id) && <span style={{ marginLeft: '8px' }}>✓</span>}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

// ── Quick Create Series Modal ─────────────────────────────────────────────────
function QuickCreateSeriesModal({ initialName, onCreated, onClose }) {
    const [name, setName] = useState(initialName || '');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    const handleSave = async () => {
        if (!name.trim()) { setError('Series name is required'); return; }
        setSaving(true);
        setError(null);
        try {
            const payload = { name: name.trim(), event_ids: [], promoter_ids: [] };
            const { tokens } = await fetchAuthSession();
            const idToken = tokens.idToken.toString();
            const res = await axios.post(`${config.API_URL}/festivals`, payload, { headers: { Authorization: idToken } });
            const fest = res.data.festival || res.data;
            onCreated({ id: fest.id, name: name.trim() });
        } catch (err) {
            setError('Failed to create festival series. Please try again.');
            setSaving(false);
        }
    };

    const handleBackdropClick = (e) => { if (e.target === e.currentTarget) onClose(); };

    return (
        <div onClick={handleBackdropClick} style={modalStyles.backdrop}>
            <div style={{ ...modalStyles.modal, width: '420px' }}>
                <div style={modalStyles.header}>
                    <span style={modalStyles.title}>Create New Series</span>
                    <button onClick={onClose} style={modalStyles.closeBtn}>✕</button>
                </div>
                {error && <div style={modalStyles.error}>{error}</div>}
                <div style={modalStyles.field}>
                    <label style={modalStyles.label}>Series Name *</label>
                    <input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Bonnaroo, Coachella..." autoFocus style={modalStyles.input} />
                </div>
                <p style={{ fontSize: '12px', color: '#777', margin: '0 0 16px 0' }}>This creates a standalone festival with no dates or events of its own — individual occurrences can be assigned to it afterward.</p>
                <div style={modalStyles.actions}>
                    <button onClick={onClose} style={modalStyles.cancelBtn}>Cancel</button>
                    <button onClick={handleSave} disabled={saving} style={modalStyles.saveBtn}>{saving ? 'Creating...' : 'Create Series'}</button>
                </div>
            </div>
        </div>
    );
}

const modalStyles = {
    backdrop: { position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
    modal: { backgroundColor: '#1e1e1e', border: '1px solid #444', borderRadius: '10px', padding: '28px 32px', width: '580px', maxWidth: '95vw', maxHeight: '90vh', overflowY: 'auto' },
    header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' },
    title: { fontSize: '18px', fontWeight: 'bold', color: '#e0e0e0' },
    closeBtn: { background: 'none', border: 'none', color: '#777', fontSize: '18px', cursor: 'pointer', lineHeight: 1 },
    field: { marginBottom: '16px' },
    label: { display: 'block', fontSize: '12px', color: '#aaa', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' },
    input: { backgroundColor: '#2c2c2c', border: '1px solid #444', borderRadius: '4px', padding: '9px 12px', fontSize: '14px', color: '#e0e0e0', width: '100%', boxSizing: 'border-box' },
    error: { backgroundColor: '#2c1a1e', border: '1px solid #cf6679', borderRadius: '4px', padding: '10px 14px', color: '#cf6679', marginBottom: '16px', fontSize: '13px' },
    actions: { display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '24px' },
    cancelBtn: { backgroundColor: 'transparent', color: '#aaa', border: '1px solid #444', borderRadius: '4px', padding: '9px 18px', fontSize: '14px', cursor: 'pointer' },
    saveBtn: { backgroundColor: '#bb86fc', color: '#121212', border: 'none', borderRadius: '4px', padding: '9px 20px', fontSize: '14px', fontWeight: 'bold', cursor: 'pointer' },
};

function EditFestival() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const [allPromoters, setAllPromoters] = useState([]);
    const [selectedPromoterIds, setSelectedPromoterIds] = useState([]);
    const [eligibleParents, setEligibleParents] = useState([]);
    const [hasChildren, setHasChildren] = useState(false);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [festivalId, setFestivalId] = useState(null);
    const [showCreateSeries, setShowCreateSeries] = useState(false);
    const [createSeriesInitialName, setCreateSeriesInitialName] = useState('');

    const [form, setForm] = useState({
        name: '',
        description: '',
        image_url: '',
        parent_festival_id: '',
    });

    useEffect(() => {
        document.title = 'SHARC - Edit Festival';
        Promise.all([
            axios.get(`${config.API_URL}/festivals/${slug}`),
            axios.get(`${config.API_URL}/promoters`),
            axios.get(`${config.API_URL}/festivals`),
        ]).then(([festRes, promotersRes, allFestRes]) => {
            const fest = festRes.data.festival;
            const festPromoters = festRes.data.promoters || [];
            setFestivalId(fest.id);
            setHasChildren(!!fest.is_parent);
            setForm({
                name: fest.name || '',
                description: fest.description || '',
                image_url: fest.image_url || '',
                parent_festival_id: fest.parent_festival_id || '',
            });
            setSelectedPromoterIds(festPromoters.map(p => p.id));
            setAllPromoters(promotersRes.data.promoters || []);
            // Eligible parents: top-level, event-free festivals only (a child
            // can't be a series, and a festival with its own events can't be
            // used as one either), excluding this festival itself. The
            // currently-assigned series (if any) stays in the list even if it
            // wouldn't otherwise qualify, so editing doesn't silently blank
            // out an existing selection.
            setEligibleParents((allFestRes.data.festivals || []).filter(f =>
                f.id !== fest.id && (
                    (!f.parent_festival_id && Number(f.event_count) === 0) ||
                    f.id === fest.parent_festival_id
                )
            ));
            setLoading(false);
        }).catch(() => {
            setError('Failed to load festival');
            setLoading(false);
        });
        return () => { document.title = 'SHARC - Shows Archive'; };
    }, [slug]);

    const handleFormChange = (e) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
    };

    const handleOpenCreateSeries = (initialName) => {
        setCreateSeriesInitialName(initialName || '');
        setShowCreateSeries(true);
    };
    const handleSeriesCreated = (newSeries) => {
        setEligibleParents(prev => [...prev, { id: newSeries.id, name: newSeries.name, parent_festival_id: null, event_count: 0 }]);
        setForm(prev => ({ ...prev, parent_festival_id: String(newSeries.id) }));
        setShowCreateSeries(false);
    };

    const handleSubmit = async () => {
        if (!form.name.trim()) { setError('Festival name is required'); return; }
        setSaving(true);
        setError(null);
        try {
            const { tokens } = await fetchAuthSession();
            const idToken = tokens.idToken.toString();
            const payload = {
                name: form.name.trim(),
                description: form.description.trim() || null,
                image_url: form.image_url.trim() || null,
                parent_festival_id: hasChildren ? null : (form.parent_festival_id || null),
                promoter_ids: selectedPromoterIds,
            };
            const res = await axios.put(`${config.API_URL}/festivals/${festivalId}`, payload, { headers: { Authorization: idToken } });
            const updatedSlug = res.data.festival ? res.data.festival.slug : festivalId;
            navigate(`/festivals/${updatedSlug}`);
        } catch (err) {
            setError('Failed to save festival');
            setSaving(false);
        }
    };

    if (loading) return <div style={styles.message}>Loading...</div>;

    return (
        <div style={styles.container}>
            <Link to={`/festivals/${slug}`} style={styles.backLink}>← Back to Festival</Link>
            <h1 style={styles.pageTitle}>Edit Festival</h1>

            {error && <div style={styles.error}>{error}</div>}

            <div style={styles.form}>
                <div style={styles.field}>
                    <label style={styles.label}>Festival Name *</label>
                    <input
                        type="text"
                        name="name"
                        value={form.name}
                        onChange={handleFormChange}
                        style={styles.input}
                    />
                </div>

                <div style={styles.field}>
                    <label style={styles.label}>Festival Series</label>
                    {hasChildren ? (
                        <div style={styles.hint}>This festival already has child festivals of its own, so it can't be made a child festival. Remove its children first if you need to change this.</div>
                    ) : (
                        <>
                            <SearchableSelect
                                options={eligibleParents}
                                value={form.parent_festival_id}
                                onChange={val => setForm(prev => ({ ...prev, parent_festival_id: val }))}
                                placeholder="Search festivals..."
                                renderLabel={f => f.name}
                                onCreateItem={handleOpenCreateSeries}
                                createLabel="Create new series"
                            />
                            <div style={styles.hint}>If this festival is a specific occurrence of a recurring series, select the series or create a new series here.</div>
                        </>
                    )}
                </div>

                <div style={styles.field}>
                    <label style={styles.label}>Promoter(s)</label>
                    <PromoterPicker promoters={allPromoters} selectedIds={selectedPromoterIds} onChange={setSelectedPromoterIds} />
                </div>

                <div style={styles.field}>
                    <label style={styles.label}>Description</label>
                    <textarea
                        name="description"
                        value={form.description}
                        onChange={handleFormChange}
                        style={styles.textarea}
                        rows={4}
                    />
                </div>

                <div style={styles.field}>
                    <label style={styles.label}>Image URL</label>
                    <input
                        type="text"
                        name="image_url"
                        value={form.image_url}
                        onChange={handleFormChange}
                        placeholder="https://..."
                        style={styles.input}
                    />
                    {form.image_url && (
                        <img
                            src={form.image_url}
                            alt="Festival preview"
                            style={styles.imagePreview}
                            onError={e => { e.target.style.display = 'none'; }}
                        />
                    )}
                </div>

                <button onClick={handleSubmit} disabled={saving} style={styles.submitButton}>
                    {saving ? 'Saving...' : 'Save Changes'}
                </button>
            </div>

            {showCreateSeries && (
                <QuickCreateSeriesModal
                    initialName={createSeriesInitialName}
                    onCreated={handleSeriesCreated}
                    onClose={() => setShowCreateSeries(false)}
                />
            )}
        </div>
    );
}

const styles = {
    container: { maxWidth: '700px', margin: '0 auto' },
    backLink: { color: '#bb86fc', textDecoration: 'none', fontSize: '14px', display: 'inline-block', marginBottom: '24px' },
    pageTitle: { fontSize: '32px', fontWeight: 'bold', color: '#e0e0e0', marginBottom: '32px' },
    form: { display: 'flex', flexDirection: 'column', gap: '24px' },
    field: { display: 'flex', flexDirection: 'column', gap: '8px' },
    twoCol: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' },
    label: { fontSize: '14px', fontWeight: '600', color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.5px' },
    hint: { fontSize: '12px', color: '#777', marginTop: '2px' },
    input: { backgroundColor: '#2c2c2c', border: '1px solid #444', borderRadius: '6px', padding: '10px 14px', fontSize: '14px', color: '#e0e0e0', width: '100%', boxSizing: 'border-box' },
    textarea: { backgroundColor: '#2c2c2c', border: '1px solid #444', borderRadius: '6px', padding: '10px 14px', fontSize: '14px', color: '#e0e0e0', width: '100%', boxSizing: 'border-box', resize: 'vertical', fontFamily: 'inherit' },
    imagePreview: { width: '120px', height: '120px', objectFit: 'cover', borderRadius: '6px', border: '1px solid #333', marginTop: '8px' },
    submitButton: { backgroundColor: '#bb86fc', color: '#121212', border: 'none', borderRadius: '6px', padding: '14px', fontSize: '16px', fontWeight: 'bold', cursor: 'pointer', marginTop: '8px' },
    error: { backgroundColor: '#2a1a1a', border: '1px solid #cf6679', borderRadius: '6px', padding: '12px 16px', color: '#cf6679', fontSize: '14px', marginBottom: '16px' },
    message: { textAlign: 'center', color: '#aaa', fontSize: '16px', padding: '60px 0' },
};

export default EditFestival;
